import { Card } from "@/components/ui/card";
import { Download, Printer } from "lucide-react";
import { AccionesMenu } from "@/components/shell/AccionesMenu";
import { TarjetaPeriodo, TarjetaTotal } from "@/components/shell/TarjetasPeriodo";
import { exportarCSV, fmtMXN, type Tabla } from "@/lib/amortizacion";

interface Props {
  tabla: Tabla;
  nombre: string;
  conFecha: boolean;
}

export function TablaAmortizacion({ tabla, nombre, conFecha }: Props) {
  return (
    <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-surface px-5 py-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
          Tabla de amortización
        </h3>
        <AccionesMenu
          acciones={[
            {
              label: "Exportar CSV",
              icono: <Download className="h-4 w-4" />,
              onClick: () => exportarCSV(nombre, tabla, conFecha),
            },
            {
              label: "Imprimir / PDF",
              icono: <Printer className="h-4 w-4" />,
              variant: "secondary",
              onClick: () => window.print(),
            },
          ]}
        />
      </div>
      {/* Móvil y tablet: una tarjeta por período */}
      <div className="solo-movil space-y-3 p-4 lg:hidden">
        {tabla.periodos.map((p) => (
          <TarjetaPeriodo
            key={p.periodo}
            titulo={`Período ${p.periodo}`}
            fecha={conFecha ? (p.fecha ?? "Sin fecha") : undefined}
            destacado={{ l: "Cuota", v: fmtMXN(p.cuota) }}
            resumen={[
              { l: "Saldo inicial", v: fmtMXN(p.saldoInicial) },
              { l: "Saldo final", v: fmtMXN(p.saldoFinal) },
            ]}
            detalle={[
              { l: "Interés del período", v: fmtMXN(p.interes), tono: "chart3" },
              { l: "Capital amortizado", v: fmtMXN(p.capital), tono: "emerald" },
              { l: "Cuota total", v: fmtMXN(p.cuota) },
            ]}
          />
        ))}
        <TarjetaTotal
          datos={[
            { l: "Total intereses", v: fmtMXN(tabla.totalIntereses), tono: "chart3" },
            { l: "Total capital", v: fmtMXN(tabla.totalCapital), tono: "emerald" },
            { l: "Total pagado", v: fmtMXN(tabla.totalPagado) },
          ]}
        />
      </div>

      <div className="solo-desktop hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="bg-navy text-primary-foreground">
              <th className="px-3 py-2.5 text-left font-medium">Período</th>
              {conFecha && <th className="px-3 py-2.5 text-left font-medium">Fecha</th>}
              <th className="px-3 py-2.5 text-right font-medium">Saldo inicial</th>
              <th className="px-3 py-2.5 text-right font-medium">Interés</th>
              <th className="px-3 py-2.5 text-right font-medium">Capital</th>
              <th className="px-3 py-2.5 text-right font-medium">Cuota total</th>
              <th className="px-3 py-2.5 text-right font-medium">Saldo final</th>
            </tr>
          </thead>
          <tbody>
            {tabla.periodos.map((p) => (
              <tr key={p.periodo} className="border-b last:border-0 odd:bg-surface/60">
                <td className="px-3 py-2 num">{p.periodo}</td>
                {conFecha && <td className="px-3 py-2 text-muted-foreground">{p.fecha ?? "—"}</td>}
                <td className="px-3 py-2 text-right num">{fmtMXN(p.saldoInicial)}</td>
                <td className="px-3 py-2 text-right num text-chart-3">{fmtMXN(p.interes)}</td>
                <td className="px-3 py-2 text-right num text-emerald">{fmtMXN(p.capital)}</td>
                <td className="px-3 py-2 text-right num font-semibold">{fmtMXN(p.cuota)}</td>
                <td className="px-3 py-2 text-right num">{fmtMXN(p.saldoFinal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-navy-soft font-semibold text-navy">
              <td className="px-3 py-2.5" colSpan={conFecha ? 3 : 2}>
                TOTAL
              </td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(tabla.totalIntereses)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(tabla.totalCapital)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(tabla.totalPagado)}</td>
              <td className="px-3 py-2.5 text-right num">{fmtMXN(0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}
